import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import { type Control, Controller, type SubmitHandler, useForm } from 'react-hook-form'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import { useAtom, useSetAtom } from 'jotai/react'
import { atom } from 'jotai'

import { BottomButtons, FormControlWithLabelWrapper, Hint } from '../wizard-common'
import {
    DISTRIBUTION_FORM_SUPPLIERS,
    DISTRIBUTION_RATE_TABLE,
    CEZ_REGIONS,
    type DistributionElectricity,
    type DistributionElectricityFormInput,
    type DistributionElectricity_Supplier,
    distributionRateHasHDO,
    supplierHasHDO,
} from '../../../../types'
import { PreviousStepButton } from '../../components'
import CustomButton from '../../../../components/custom-button'
import {
    useCreateDistributionElectricity,
    useDistributionElectricity,
    useUpdateDistributionElectricity,
} from '../../../../hooks/use-distribution-electricity'
import { useAppliance } from '../../../../hooks/use-appliance'
import ApplianceLoader from '@/wrappers/appliance-loader'
import ApplianceHdoDependencyToggle from '@/components/appliance-hdo-dependency-toggle'

const distributionFormWarnings = atom<string>('')

export function DistributorForm({
    applianceId,
    estateId,
    distributionElectricityId,
    goBack,
    nextStep,
}: {
    applianceId: number
    estateId: number
    distributionElectricityId: number | undefined | null
    goBack: () => void
    nextStep: () => void
}) {
    const setWarnings = useSetAtom(distributionFormWarnings)

    const { status, data, error } = useAppliance(applianceId, {
        refetchOnMount: false, // @hotfix infinite refetch caused by mounts triggering it
    })

    if (status === 'pending') {
        return <CircularProgress />
    }

    if (error) {
        return (
            <Box>
                <ErrorOutlineRoundedIcon />
                <Box>{error.message}</Box>
            </Box>
        )
    }

    return (
        <>
            {distributionElectricityId ?
                <Update
                    applianceId={applianceId}    
                    distributionElectricityId={distributionElectricityId}
                    goBack={() => {
                        setWarnings('')
                        goBack()
                    }}
                    nextStep={() => {
                        setWarnings('')
                        nextStep()
                    }}
                    isDependentOnHdo={data.is_dependent_on_hdo}
                />
            :
                <Create
                    applianceId={applianceId}
                    estateId={estateId}
                    goBack={() => {
                        setWarnings('')
                        goBack()
                    }}
                    nextStep={() => {
                        setWarnings('')
                        nextStep()
                    }}
                    isDependentOnHdo={data.is_dependent_on_hdo}
                />
            }
        </>
    )
}

function Create({
    applianceId,
    estateId,
    goBack,
    nextStep,
    isDependentOnHdo,
}: {
    applianceId: number
    estateId: number
    goBack: () => void
    nextStep: () => void
    isDependentOnHdo: boolean
}) {
    const [warnings, setWarnings] = useAtom(distributionFormWarnings)

    const mutation = useCreateDistributionElectricity(estateId, data => {
        setWarnings(data.warnings || '')
        nextStep()
    })

    const onSubmit: SubmitHandler<DistributionElectricityFormInput> = data => {
        mutation.mutate(data)
    }

    return (
        <Form
            applianceId={applianceId}    
            goBack={goBack}
            onSubmit={onSubmit}
            disableSubmit={mutation.isPending}
            isDependentOnHdo={isDependentOnHdo}
            warnings={warnings}
        />
    )
}

function Update({
    applianceId,
    distributionElectricityId,
    goBack,
    nextStep,
    isDependentOnHdo,
}: {
    applianceId: number
    distributionElectricityId: number
    goBack: () => void
    nextStep: () => void
    isDependentOnHdo: boolean
}) {
    const [warnings, setWarnings] = useAtom(distributionFormWarnings)

    const { status, data, error } = useDistributionElectricity(distributionElectricityId)
    const mutation = useUpdateDistributionElectricity(distributionElectricityId, data => {
        setWarnings(data.warnings || '')
        nextStep()
    })

    if (status === 'pending') {
        return <CircularProgress />
    }

    if (error) {
        return (
            <Box>
                <ErrorOutlineRoundedIcon />
                <Box>{error.message}</Box>
            </Box>
        )
    }

    const onSubmit: SubmitHandler<DistributionElectricityFormInput> = data => {
        mutation.mutate(data)
    }

    return (
        <Form
            applianceId={applianceId}
            isDependentOnHdo={isDependentOnHdo}
            data={data}
            goBack={goBack}
            onSubmit={onSubmit}
            disableSubmit={mutation.isPending}
            warnings={warnings}
        />
    )
}

function Form({
    applianceId,
    onSubmit,
    data, // initial data
    goBack,
    disableSubmit,
    warnings,
}: {
    applianceId: number
    onSubmit: SubmitHandler<DistributionElectricityFormInput>
    data?: DistributionElectricity
    goBack: () => void
    disableSubmit: boolean
    isDependentOnHdo: boolean
    warnings: string
}) {

    const { control, handleSubmit, watch } = useForm<DistributionElectricityFormInput>({
        defaultValues: {
            supplier: data?.supplier || '',
            distribution_rate: data?.distribution_rate || '',
            signal_code: data?.signal_code || '',
            region: data?.region || 'zapad',
        },
    })

    const supplier: DistributionElectricityFormInput['supplier'] | undefined = watch('supplier')
    const distribution_rate: DistributionElectricityFormInput['distribution_rate'] | undefined =
        watch('distribution_rate')

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2.5rem',
                }}
            >
                <FormControlWithLabelWrapper
                    label={'Vyberte Vašeho distributora.'}
                    labelId='DistributorForm-supplier'
                >
                    <Controller
                        name="supplier"
                        control={control}
                        render={({ field }) => (
                            <Select
                                label="Distributor"
                                id="DistributorForm-supplier"
                                {...field}
                            >
                                {DISTRIBUTION_FORM_SUPPLIERS.map(({ name, id }) => (
                                    <MenuItem key={id} value={id}>
                                        {name}
                                    </MenuItem>
                                ))}
                            </Select>
                        )}
                    />
                </FormControlWithLabelWrapper>

                <FormControlWithLabelWrapper
                    label={'Vyberte distribuční sazbu.'}
                    labelId='DistributorForm-distribution_rate'
                >
                    <Controller
                        name="distribution_rate"
                        control={control}
                        render={({ field }) => (
                            <Select
                                // labelId="appliance-energy-class-label"
                                label="Distribuční sazba"
                                id="DistributorForm-distribution_rate"
                                {...field}
                            >
                                {DISTRIBUTION_RATE_TABLE.map(({ name, id }) => (
                                    <MenuItem key={id} value={id}>
                                        {name}
                                    </MenuItem>
                                ))}
                            </Select>
                        )}
                    />
                </FormControlWithLabelWrapper>

                <DistributionRate
                    supplier={supplier}
                    distribution_rate={distribution_rate}
                    control={control}
                    warnings={warnings}
                />

                <ApplianceLoader applianceId={applianceId}>
                    {appliance => (
                        <ApplianceHdoDependencyToggle
                            applianceId={applianceId}
                            isDependentOnHdo={appliance.is_dependent_on_hdo}
                        />
                    )}
                </ApplianceLoader>

                <BottomButtons>
                    <PreviousStepButton
                        onClick={() => {
                            goBack()
                        }}
                    >
                        Zpět
                    </PreviousStepButton>

                    <CustomButton
                        text="Pokračovat"
                        type="submit"
                        disabled={disableSubmit}
                    />
                </BottomButtons>
            </Box>
        </form>
    )
}

function WhereToFindHDO({
    supplier,
}: {
    supplier: DistributionElectricity_Supplier | undefined | ''
}) {
    // 'https://www.predistribuce.cz/cs/potrebuji-zaridit/zakaznici/stav-hdo/'
    return (
        <Box sx={{}}>
            {/* {
                // supplier === 'ČEZ' && <a href={"https://www.cezdistribuce.cz/cs/pro-zakazniky/spinani-hdo.html"} target={"_blank"}>Kde najít povel a kód HDO</a>
                supplier === 'ČEZ' && <a href={"https://dip.cezdistribuce.cz/irj/portal/anonymous/casy-spinani/"} target={"_blank"}>Zkuste vyhledat své HDO podle EAN</a>
            } */}
            {supplier === 'PRE' && (
                <a
                    href={'https://www.predistribuce.cz/cs/potrebuji-zaridit/zakaznici/stav-hdo'}
                    target={'_blank'}
                    rel="noreferrer"
                >
                    Kde najít kód HDO
                </a>
            )}
            {supplier === 'EG.D' && (
                <a
                    href={'https://www.egd.cz/jak-vyhledat-kod-hdo'}
                    target={'_blank'}
                    rel="noreferrer"
                >
                    Kde najít povel a kód HDO
                </a>
            )}
        </Box>
    )
}

function DistributionRate({
    control,
    supplier,
    distribution_rate,
    warnings,
}: {
    control: Control<DistributionElectricityFormInput, any>
    supplier: DistributionElectricityFormInput['supplier']
    distribution_rate: DistributionElectricityFormInput['distribution_rate']
    warnings?: string
}) {

    // These tarifs do not have HDO (https://chytrybojler.cz/tutorials/)
    if (!distributionRateHasHDO(distribution_rate)) return

    // 'https://www.predistribuce.cz/cs/potrebuji-zaridit/zakaznici/stav-hdo/'

    // Using `display: none` solves this RHF bug:
    //  select picks another value -> this function returns other input -> in render the field has correct `name` but `value` of previous field
    return (
        <Box>
            {supplier === 'ČEZ' ?
                <Box>
                    <FormControlWithLabelWrapper
                        label={'Region ČEZ'}
                        labelId='DistributorForm-region'
                        hint='Vyberte váš region dle dostupnosti produktu ČEZ'
                    >
                        <Controller
                            name="region"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    label="Region ČEZ"
                                    id="DistributorForm-region"
                                    {...field}
                                >
                                    {CEZ_REGIONS.map(({ name, id }) => (
                                        <MenuItem key={id} value={id}>
                                            {name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            )}
                        />
                    </FormControlWithLabelWrapper>

                    <FormControlWithLabelWrapper
                        label={'Kód HDO signálu'}
                        labelId='DistributorForm-signal_code'
                        hint='Zadejte kód signálu HDO podle kterého se spíná váš bojler'
                    >
                        <Hint>
                            <strong>Příklady kódů:</strong>
                            <br />• Přímotop: PTV1, PTV2, PTV3
                            <br />• Standardní: A1B6DP1, A2B6DP2
                            <br />
                            <br />Kód HDO najdete v dokumentaci k vašemu měřidlu nebo ve smlouvě s ČEZ Distribuce.
                            <br />Prosíme respektujte smlouvu se svým distributorem o časech spínání.
                        </Hint>

                        <Controller
                            name="signal_code"
                            control={control}
                            render={({ field }) => {
                                return (
                                    <TextField
                                        id="DistributorForm-signal_code"
                                        variant="outlined"
                                        placeholder="např. PTV2 nebo A1B6DP1"
                                        type="text"
                                        required={true}
                                        fullWidth
                                        sx={{ mt: 1 }}
                                        {...field}
                                    />
                                )
                            }}
                        />
                    </FormControlWithLabelWrapper>
                </Box>
                :
                <Box sx={{ display: supplierHasHDO(supplier) ? 'block' : 'none' }}>
                    <FormControlWithLabelWrapper
                        label={'Kód HDO'}
                        labelId='DistributorForm-signal_code'
                    >
                        <Hint>
                            Pro jižní Čechy je to třímístné číslo. Pro jižní Moravu kombinace písmen A,
                            B, P a čísel, např. A1B8P1.
                        </Hint>
                        <WhereToFindHDO supplier={supplier} />

                        <Controller
                            name="signal_code"
                            control={control}
                            render={({ field }) => {
                                return (
                                    <TextField
                                        id="DistributorForm-signal_code"
                                        variant="standard"
                                        type="text"
                                        required={supplierHasHDO(supplier)}
                                        disabled={!supplierHasHDO(supplier)}
                                        {...field}
                                    />
                                )
                            }}
                        />
                    </FormControlWithLabelWrapper>
                </Box>
            }

            <Box sx={{ color: '#f70500', margin: '1.5rem 0 0 0' }}>{warnings}</Box>
        </Box>
    )
}
